import { isEqual } from 'lodash';

import { BasicColumnTypes } from '@opengov/ogql-types';

import {
  createAccountSummary,
  createMultiHierarchyAccountSummary,
  IAccountSummary,
  IColumnDataSource,
  IColumnSource,
  IRowOverride,
  isMultiHierarchyVariant,
  MultiHierarchyAccountSummary
} from './accountSummary';
import { IIdentifiable } from './identity';
import { createRecord, DeepPartial } from './record';
import { CalendarUnit } from './reportColumns';
import {
  createDimensionalDateReportColumn,
  createDimensionalReportColumn,
  createStorageReportColumn,
  EColumnDimensionType,
  IAggregateReportColumn,
  IConditionalReportColumn,
  IDimensionalDateReportColumn,
  IDimensionalReportColumn,
  IPercentOfTotalReportColumn,
  IReportColumn,
  isFYFPColumnFilter,
  IStorageReportColumn,
  ITargetStatusReportColumn,
  mapColumnToFactory
} from './reportColumns-immer';
import {
  createColumnFilter,
  createColumnFilterBooleanOperator,
  createSegmentFilter,
  IColumnFilter,
  IColumnFilterBooleanOperator,
  ISegmentFilter
} from './reportFilters';

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
  None = 'None'
}

export enum LegendSortOptions {
  Asc = 'ASC',
  Desc = 'DESC',
  LargestToSmallest = 'LARGEST',
  SmallestToLargest = 'SMALLEST'
}

export enum StackingOptions {
  None = 'NONE',
  Standard = 'STANDARD'
}

export enum ColumnTypes {
  CALCULATION = 'Calculation',
  HACK = 'hack'
}

export interface IReportConfigurationVisualizationQuery extends IReportConfigurationDimensionalQuery {
  categorizedByColumn: IReportColumn;
  chartType: EReportChartType;
  filter: IColumnFilter[];
  filterBooleanOperators: IColumnFilterBooleanOperator[];
  hiddenControls: IHiddenControlColumns[];
  organizedBy: IOrganizedBy;
  showColumn:
    | IDimensionalReportColumn
    | IStorageReportColumn
    | IPercentOfTotalReportColumn
    | IAggregateReportColumn
    | IConditionalReportColumn<any>
    | ITargetStatusReportColumn;
  showColumnAsPercentOfTotal?: boolean;
}

export interface IReportConfigurationDetailTableQuery {
  columns: IReportColumn[];
  filter: IColumnFilter[];
  filterBooleanOperators: IColumnFilterBooleanOperator[];
  paginationLimit: number;
  paginationOffset: number;
  searchTableQuery?: string;
  segmentFilters?: ISegmentFilter[];
  searchTableQueryColumn?: IReportColumn;
  sortColumn?: string;
  sortDirection?: SortDirection;
  groupByColumns?: string[];
  showTotals?: boolean;
}

export interface IReportConfigurationDimensionalQuery {
  columnsColumn: IDimensionalReportColumn | IDimensionalDateReportColumn | IStorageReportColumn;
  rowsColumn: IDimensionalReportColumn;
  showAccountType: string;
}

const reportConfigurationDimensionalQueryRecord = createRecord<IReportConfigurationDimensionalQuery>({
  showAccountType: '',
  rowsColumn: null,
  columnsColumn: null
});

export const createReportConfigurationDimensionalQuery = (
  values: Partial<IReportConfigurationDimensionalQuery> = {}
): IReportConfigurationDimensionalQuery => ({
  ...reportConfigurationDimensionalQueryRecord(values),
  columnsColumn: values.columnsColumn
    ? (values.columnsColumn as IDimensionalReportColumn).dimensionType
      ? (values.columnsColumn as IDimensionalReportColumn).dimensionType === EColumnDimensionType.DATE
        ? createDimensionalDateReportColumn(values.columnsColumn as IDimensionalDateReportColumn)
        : createDimensionalReportColumn(values.columnsColumn as IDimensionalReportColumn)
      : createStorageReportColumn(values.columnsColumn as IStorageReportColumn)
    : null
});

export interface IReportConfigurationDimensionalSummaryQuery {
  amountColumnName: string;
  dimension: IReportConfigurationDimensionalQuery;
  segmentFilters?: ISegmentFilter[];
  globalSegmentFilters?: ISegmentFilter[];
  sortColumnName?: string;
  sortDirection?: SortDirection;
  filter?: IColumnFilter[];
}

const reportConfigurationDimensionalSummaryQueryRecord = createRecord<IReportConfigurationDimensionalSummaryQuery>({
  amountColumnName: 'amount', // this is so we don't have to migrate old dimensional report configs
  segmentFilters: [],
  globalSegmentFilters: [],
  filter: [],
  sortColumnName: '',
  sortDirection: SortDirection.None,
  dimension: createReportConfigurationDimensionalQuery()
});

export const createReportConfigurationDimensionalSummaryQuery = (
  values: Partial<IReportConfigurationDimensionalSummaryQuery> = {}
): IReportConfigurationDimensionalSummaryQuery => ({
  ...reportConfigurationDimensionalSummaryQueryRecord(values),
  dimension: values.dimension
    ? createReportConfigurationDimensionalQuery(values.dimension)
    : createReportConfigurationDimensionalQuery()
});

export enum ReportTableTabs {
  Summary = 'summary',
  Details = 'details'
}

export interface IReportConfigurationPresentationSummary {
  sortColumnName: string;
  sortDirection: SortDirection;
}

export const createColorMap = (
  colors: { [key: string]: Partial<ISingleColor> } = {}
): { [key: string]: ISingleColor } =>
  Object.fromEntries(
    Object.entries(colors).map(([key, color]: [string, Partial<ISingleColor>]): [string, ISingleColor] => [
      key,
      createSingleColor(color)
    ])
  );

export const createSingleColor = (values: Partial<ISingleColor> = {}): ISingleColor => ({
  hexValue: '',
  ...values,
  type: 'custom'
});
export interface IReportConfigurationPresentation {
  colors: { [categoryName: string]: ISingleColor };
  columnNames: IColumnAlias[];
  dimensionalSummaryBlockNames: IControlBlockAlias[];
  dimensionalSummaryBlockExpandedStates: IControlBlockIndex[];
  groupByExpandedNodes: string[];
  entityFiscalYearStartMonth: number;
  reportTableActiveTab: ReportTableTabs;
  summary: IReportConfigurationPresentationSummary;
  legendSortOrder?: LegendSortOptions;
  hiddenLegendItems?: string[];
  agGridColumnConfig?: IAgGridColumnConfig[];
  stacking?: StackingOptions;
  labelsEnabled?: boolean;
  axisTitlesEnabled?: boolean;
}

export interface ISingleColor {
  name?: string;
  type: 'openGovPalette' | 'entityPalette' | 'custom';
  hexValue: string;
}

export interface IReportConfigurationLayers {
  target?: IReportTarget;
}

export interface ITargetLocale {
  target: string;
  conditions: {
    greaterThan: string;
    greaterThanOrEqualTo: string;
    lessThan: string;
    lessThanOrEqualTo: string;
    equalTo: string;
    notEqualTo: string;
  };
  per: string;
}

export interface IReportTarget {
  type?: 'nextGen';
  conditions: IReportTargetCondition[];
  elseStatus: IReportTargetStatus;
}

export interface IReportTargetCondition {
  operator: EReportTargetComparisonOperators;
  value: IReportTargetValue;
  status: IReportTargetStatus;
}

export enum EReportTargetComparisonOperators {
  greaterThan = 'greaterThan',
  greaterThanOrEqualTo = 'greaterThanOrEqualTo',
  lessThan = 'lessThan',
  lessThanOrEqualTo = 'lessThanOrEqualTo',
  equalTo = 'equalTo',
  notEqualTo = 'notEqualTo',
  between = 'between'
}

export type EReportTargetOperatorSymbolMap = {
  [K in EReportTargetComparisonOperators]: string;
};

export interface IReportTargetValue {
  valueType: EReportTargetValueTypes;
  text: string;
}

export enum EReportTargetValueTypes {
  currency = 'currency',
  number = 'number',
  percent = 'percent'
}

export interface IReportTargetStatus {
  text: string;
}

interface IBaseReportConfiguration {
  reportId: string;
  viewId: string;
  presentationConfiguration: IReportConfigurationPresentation;
  visualizationQuery: IReportConfigurationVisualizationQuery;
  detailTableQuery: IReportConfigurationDetailTableQuery;
  layers: IReportConfigurationLayers;
  dimensionalSummaryQueries: IReportConfigurationDimensionalSummaryQuery[];
  accountSummary?: IAccountSummary | MultiHierarchyAccountSummary;
}

export enum OrganizedByColumnType {
  Date = BasicColumnTypes.Date,
  Text = BasicColumnTypes.Text,
  Period = BasicColumnTypes.Period
}

export type IDeletedReferenceUsage =
  | {
      location: string;
      status?: 'removed';
      replacedWith?: { name: string; label: string };
      dependents?: string[];
    }
  | {
      location: string;
      status: 'retained-but-invalid';
      retainedColumnGroupIds: string[];
      dependents?: string[];
    };

export type IDeletedReferenceInfo = {
  name: string;
  label: string;
  source: 'coa' | 'dataset';
  usedAs: IDeletedReferenceUsage[];
};

export type IDeletedReference = {
  metadata?: {
    valid: boolean;
    // deletedColumns should be renamed to deletedReferences
    deletedColumns: IDeletedReferenceInfo[];
    deletedRowGroupings?: Record<number, number[]>;
  };
};
export type IReportConfiguration = IIdentifiable & IBaseReportConfiguration & IDeletedReference;

export interface IOrganizedBy {
  column: IReportColumn;
  columnType: OrganizedByColumnType;
  selection: CalendarUnit | string;
}

const organizedByRecord = createRecord<IOrganizedBy>({
  columnType: OrganizedByColumnType.Date,
  selection: CalendarUnit.FiscalYear,
  column: createStorageReportColumn()
});

export const createOrganizedBy = (values: DeepPartial<IOrganizedBy> = {}): IOrganizedBy => ({
  ...organizedByRecord(values),
  column: values.column ? mapColumnToFactory(values.column) : createStorageReportColumn()
});

export enum EReportChartType {
  VerticalBar = 'bar',
  HorizontalBar = 'horizontal_bar',
  Line = 'line',
  Pie = 'pie'
}

const reportConfigurationDetailTableQueryRecord = createRecord<IReportConfigurationDetailTableQuery>({
  paginationLimit: 25,
  paginationOffset: 0,
  searchTableQuery: '',
  sortColumn: '',
  sortDirection: SortDirection.ASC,
  groupByColumns: [],
  showTotals: true,
  columns: [],
  filter: [],
  filterBooleanOperators: [],
  searchTableQueryColumn: createStorageReportColumn(),
  segmentFilters: []
});

export const createDetailTableQuery = (
  values: DeepPartial<IReportConfigurationDetailTableQuery> = {}
): IReportConfigurationDetailTableQuery => ({
  ...reportConfigurationDetailTableQueryRecord(values),
  columns: values.columns ? values.columns.map(mapColumnToFactory) : [],
  filter: values.filter ? values.filter.map(createColumnFilter) : [],
  filterBooleanOperators: values.filterBooleanOperators
    ? values.filterBooleanOperators.map(createColumnFilterBooleanOperator)
    : [],
  searchTableQueryColumn: values.searchTableQueryColumn
    ? mapColumnToFactory(values.searchTableQueryColumn)
    : createStorageReportColumn(),
  segmentFilters: values.segmentFilters ? values.segmentFilters.map(createSegmentFilter) : []
});

export enum ReportControlType {
  By = 'by',
  Show = 'show',
  Categorize = 'categorize'
}

export interface IHiddenControlColumns {
  type: ReportControlType;
  columnName: string;
}

export const createHiddenControlColumns = (values: Partial<IHiddenControlColumns> = {}): IHiddenControlColumns => ({
  type: null,
  columnName: '',
  ...values
});

export interface IAgGridColumnConfig {
  sortedIndex: number;
  columnName: string;
  pinned: string | boolean;
  hide: boolean;
}

const agGridColumnConfigRecord = createRecord<IAgGridColumnConfig>({
  sortedIndex: null,
  columnName: '',
  pinned: null,
  hide: false
});

export const createAgGridColumnConfig = agGridColumnConfigRecord;

export interface INameAlias {
  originalName: string;
  displayName: string;
}

export interface IColumnAlias extends INameAlias {
  /**
   * @deprecated this doesn't seem to be used anywhere, we should remove it
   */
  columnIndex: number;
}

export const createColumnAlias = (values: Partial<IColumnAlias> = {}): IColumnAlias => ({
  columnIndex: 0,
  originalName: '',
  displayName: '',
  ...values
});

export interface IControlBlockIndex {
  controlBlockIndex: number;
}

export type IControlBlockAlias = INameAlias & IControlBlockIndex;

export const createControlBlockIndex = (values: Partial<IControlBlockIndex> = {}): IControlBlockIndex => ({
  controlBlockIndex: null,
  ...values
});

export const createControlBlockAlias = (values: Partial<IControlBlockAlias> = {}): IControlBlockAlias => ({
  controlBlockIndex: 0,
  originalName: '',
  displayName: '',
  ...values
});

export const createReportTarget = (values: DeepPartial<IReportTarget> = {}): IReportTarget => ({
  conditions: [],
  elseStatus: null,
  ...(values as unknown as IReportTarget)
});

export const createReportConfigurationLayers = (
  values: DeepPartial<IReportConfigurationLayers> = {}
): IReportConfigurationLayers => ({
  target: createReportTarget(),
  ...(values as unknown as IReportConfigurationLayers)
});

export const createReportTargetValue = createRecord<IReportTargetValue>({
  valueType: null,
  text: null
});

export const createReportTargetStatus = createRecord<IReportTargetStatus>({
  text: null
});

export const createReportTargetCondition = (values: Partial<IReportTargetCondition> = {}): IReportTargetCondition => ({
  operator: values.operator || null,
  value: values.value ? createReportTargetValue(values.value) : null,
  status: values.status ? createReportTargetStatus(values.status) : null
});

// comparing the salient bits
// the id may or may not match so we do not compare that
export const reportConfigurationsMatch = (current: IReportConfiguration, r: IReportConfiguration): boolean => {
  return (
    r.viewId === current.viewId &&
    r.reportId === current.reportId &&
    isEqual(r.dimensionalSummaryQueries, current.dimensionalSummaryQueries) &&
    isEqual(r.detailTableQuery, current.detailTableQuery) &&
    isEqual(r.presentationConfiguration, current.presentationConfiguration) &&
    isEqual(r.visualizationQuery, current.visualizationQuery) &&
    isEqual(r.layers, current.layers) &&
    isEqual(r.accountSummary, current.accountSummary)
  );
};

export interface IResourceMetadata {
  id: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

export type IReportConfigurationPayload = IBaseReportConfiguration;
export type IReportConfigurationResponse = IReportConfiguration & IResourceMetadata;
export type IReportConfigurationCreatePayload = IReportConfiguration;

const reportConfigurationVisualizationQueryRecord = createRecord<IReportConfigurationVisualizationQuery>({
  chartType: EReportChartType.VerticalBar,
  columnsColumn: null,
  filter: [],
  filterBooleanOperators: [],
  hiddenControls: [],
  rowsColumn: null,
  showAccountType: null,
  showColumnAsPercentOfTotal: false,
  organizedBy: createOrganizedBy(),
  categorizedByColumn: createStorageReportColumn(),
  showColumn: createStorageReportColumn()
});

export const createReportConfigurationVisualizationQuery = (
  values: DeepPartial<IReportConfigurationVisualizationQuery> = {}
): IReportConfigurationVisualizationQuery => ({
  ...reportConfigurationVisualizationQueryRecord(values),
  organizedBy: values.organizedBy ? createOrganizedBy(values.organizedBy) : createOrganizedBy(),
  categorizedByColumn: values.categorizedByColumn
    ? mapColumnToFactory(values.categorizedByColumn)
    : createStorageReportColumn(),
  showColumn: values.showColumn ? (mapColumnToFactory(values.showColumn) as any) : createStorageReportColumn()
});

const reportConfigurationPresentationRecord = createRecord<IReportConfigurationPresentation>({
  colors: {},
  columnNames: [],
  dimensionalSummaryBlockNames: [],
  dimensionalSummaryBlockExpandedStates: [],
  entityFiscalYearStartMonth: null,
  groupByExpandedNodes: [],
  reportTableActiveTab: ReportTableTabs.Summary,
  summary: { sortColumnName: '', sortDirection: SortDirection.None },
  legendSortOrder: LegendSortOptions.LargestToSmallest,
  hiddenLegendItems: [],
  agGridColumnConfig: null,
  stacking: StackingOptions.Standard,
  labelsEnabled: false,
  axisTitlesEnabled: true
});

export const createReportConfigurationPresentation = (
  values: DeepPartial<IReportConfigurationPresentation> = {}
): IReportConfigurationPresentation => ({
  ...reportConfigurationPresentationRecord(values),
  colors: createColorMap(values.colors),
  hiddenLegendItems: values.hiddenLegendItems || []
});

const reportConfigurationPayloadRecord = createRecord<IReportConfigurationPayload>({
  reportId: null,
  viewId: null,
  dimensionalSummaryQueries: [],
  presentationConfiguration: createReportConfigurationPresentation(),
  visualizationQuery: createReportConfigurationVisualizationQuery(),
  detailTableQuery: createDetailTableQuery(),
  layers: createReportConfigurationLayers(),
  accountSummary: null
});

export const createReportConfigurationPayload = (
  values: DeepPartial<IReportConfigurationPayload> = {}
): IReportConfigurationPayload => ({
  ...reportConfigurationPayloadRecord(values),
  dimensionalSummaryQueries: values.dimensionalSummaryQueries
    ? values.dimensionalSummaryQueries.map(createReportConfigurationDimensionalSummaryQuery)
    : [],
  presentationConfiguration: createReportConfigurationPresentation(values.presentationConfiguration),
  visualizationQuery: createReportConfigurationVisualizationQuery(values.visualizationQuery),
  detailTableQuery: createDetailTableQuery(values.detailTableQuery),
  layers: createReportConfigurationLayers(values.layers)
});

const reportConfigurationRecord = createRecord<IReportConfiguration>({
  id: null,
  reportId: null,
  viewId: null,
  dimensionalSummaryQueries: [],
  presentationConfiguration: createReportConfigurationPresentation(),
  visualizationQuery: createReportConfigurationVisualizationQuery(),
  detailTableQuery: createDetailTableQuery(),
  layers: createReportConfigurationLayers(),
  accountSummary: createAccountSummary()
});

export const createReportConfiguration = (values: DeepPartial<IReportConfiguration> = {}): IReportConfiguration => ({
  ...reportConfigurationRecord(values),
  dimensionalSummaryQueries: values.dimensionalSummaryQueries
    ? values.dimensionalSummaryQueries.map(createReportConfigurationDimensionalSummaryQuery)
    : [],
  presentationConfiguration: createReportConfigurationPresentation(values.presentationConfiguration),
  visualizationQuery: createReportConfigurationVisualizationQuery(values.visualizationQuery),
  detailTableQuery: createDetailTableQuery(values.detailTableQuery),
  layers: createReportConfigurationLayers(values.layers),
  ...(values?.hasOwnProperty('accountSummary') && {
    accountSummary:
      isMultiHierarchyVariant(values.accountSummary?.variant)
        ? createMultiHierarchyAccountSummary(values.accountSummary as DeepPartial<MultiHierarchyAccountSummary>)
        : createAccountSummary(values.accountSummary as DeepPartial<IAccountSummary>)
  }),
  ...(values?.metadata && { metadata: values.metadata as IDeletedReference['metadata'] })
});

const columnGroupsHasFYFPColumnFilter = (columnGroups: IColumnDataSource[] | IColumnSource[]): boolean => {
  return (columnGroups as IColumnDataSource[]).some(({ dateFilter }) => dateFilter?.some(isFYFPColumnFilter));
};

export const accountSummaryHasFYFPColumnFilter = (accountSummary: IAccountSummary): boolean => {
  if (!accountSummary.columnGroups && !accountSummary.rowGroups) return false;
  return (
    columnGroupsHasFYFPColumnFilter(accountSummary.columnGroups) ||
    accountSummary.rowGroups.some((rowGroup: IRowOverride) => {
      const columnGroups: IColumnDataSource[] = rowGroup.columnGroups ? Object.values(rowGroup.columnGroups) : [];
      return columnGroupsHasFYFPColumnFilter(columnGroups);
    })
  );
};
